import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {  faLeaf, faArrowRight, faBolt, faRecycle, faCog, faCalendarAlt, faShieldAlt, faTools, faProjectDiagram, faCogs } from '@fortawesome/free-solid-svg-icons';
import SEO from './SEO';

const Hydroelectric = () => {
  return (
    <div className="services">
      <SEO
        title="Hydroelectric Power | CoreNova Renewable Energy"
        description="Small and micro hydro solutions with smart grid integration and environmental compliance."
        canonical="https://corenovarenewable.com/services/hydro"
        keywords="hydroelectric, hydro power, micro hydro, small hydro, water turbine"
      />

      <section className="relative bg-gradient-to-r from-primary-green to-secondary-blue text-white py-20 lg:py-32 text-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/service hero.png" alt="Hydroelectric Services" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-green/40 to-secondary-blue/40"></div>
        </div>
        <div className="container relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold">Hydroelectric Power</h1>
          <p className="text-xl md:text-2xl opacity-90">Reliable, water-powered electricity generation</p>
        </div>
      </section>

      <section className="section bg-white">
  <div className="container max-w-6xl">
    <div className="text-center mb-16">
      <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4">
        Hydroelectric Power Solutions
      </h2>
      <p className="text-xl text-gray-600 max-w-3xl mx-auto">
        Harnessing the power of water for clean, sustainable energy generation
      </p>
    </div>

    <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
      <div>
        <div className="bg-gradient-to-r from-primary-green/10 to-secondary-blue/10 rounded-2xl p-8 mb-8">
          <p className="text-gray-700 leading-relaxed text-lg">
            Hydropower, or hydroelectric power, is a tried and true source of clean, renewable 
            energy that has provided the backbone of power generation for the world for more than 
            a century. It uses the kinetic power of flowing water to produce electricity without 
            combustion of fossil fuels, rendering it an essential actor in the shift to a sustainable 
            energy world.
          </p>
        </div>
        
        <div className="bg-white rounded-2xl p-6 border border-gray-200">
          <h3 className="text-2xl font-bold text-dark mb-4">The Science Behind Hydroelectric Power: A Reliable Flow of Energy</h3>
          <p className="text-gray-700 leading-relaxed">
            <a href="/srvices/hydro">Hydroelectric power</a> generation is a very fundamental and efficient process. It entails 
            the conversion of the kinetic energy of running waters into electricity without the 
            production of any direct carbon emissions.
          </p>
        </div>
      </div>
      
      <div className="relative">
        <div className="relative rounded-2xl overflow-hidden shadow-xl">
          <img 
            src="/images/hydro.jpg" 
            alt="Hydroelectric power generation - Corenova Renewable Energy" 
            className="w-full h-96 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
        </div>
        
      </div>
    </div>

    {/* Hydroelectric Process */}
    <div className="bg-gray-50 rounded-2xl p-8 lg:p-12 mb-16">
      <h3 className="text-2xl md:text-3xl font-bold text-dark mb-8 text-center">
        The Hydroelectric Power Generation Process
      </h3>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-primary-green rounded-full flex items-center justify-center mb-4 mx-auto">
            <span className="text-white font-bold text-xl">1</span>
          </div>
          <h4 className="text-lg font-bold text-dark mb-3">Water Source</h4>
          <p className="text-gray-600 text-sm">
            Utilizing natural water bodies like rivers and streams, or creating 
            reservoirs by building dams.
          </p>
        </div>
        
        <div className="text-center">
          <div className="w-16 h-16 bg-primary-green rounded-full flex items-center justify-center mb-4 mx-auto">
            <span className="text-white font-bold text-xl">2</span>
          </div>
          <h4 className="text-lg font-bold text-dark mb-3">Turbine System</h4>
          <p className="text-gray-600 text-sm">
            Channeling the stream of water into a gigantic pipe, or penstock, and 
            forcing the water through the turbine blades.
          </p>
        </div>
        
        <div className="text-center">
          <div className="w-16 h-16 bg-primary-green rounded-full flex items-center justify-center mb-4 mx-auto">
            <span className="text-white font-bold text-xl">3</span>
          </div>
          <h4 className="text-lg font-bold text-dark mb-3">Generator</h4>
          <p className="text-gray-600 text-sm">
            The generator is a rotating turbine whose purpose is converting the 
            rotational energy through electromagnetic induction into electrical energy.
          </p>
        </div>
        
        <div className="text-center">
          <div className="w-16 h-16 bg-primary-green rounded-full flex items-center justify-center mb-4 mx-auto">
            <span className="text-white font-bold text-xl">4</span>
          </div>
          <h4 className="text-lg font-bold text-dark mb-3">Power Distribution</h4>
          <p className="text-gray-600 text-sm">
            The power is thus transmitted through the power grid, on its way for 
            consumption by industries and households.
          </p>
        </div>
      </div>
    </div>

    {/* Benefits Section */}
    <div className="grid md:grid-cols-3 gap-8 mb-16">
      <div className="bg-white rounded-2xl p-6 border border-gray-200 text-center">
        <div className="w-16 h-16 bg-primary-green/10 rounded-2xl flex items-center justify-center mb-4 mx-auto">
          <FontAwesomeIcon icon={faRecycle} className="text-2xl text-primary-green" />
        </div>
        <h4 className="text-lg font-bold text-dark mb-3">Renewable & Sustainable</h4>
        <p className="text-gray-600">
          Continuous energy generation from natural water cycles without depletion
        </p>
      </div>
      
      <div className="bg-white rounded-2xl p-6 border border-gray-200 text-center">
        <div className="w-16 h-16 bg-primary-green/10 rounded-2xl flex items-center justify-center mb-4 mx-auto">
          <FontAwesomeIcon icon={faLeaf} className="text-2xl text-primary-green" />
        </div>
        <h4 className="text-lg font-bold text-dark mb-3">Zero Emissions</h4>
        <p className="text-gray-600">
          Clean energy production without greenhouse gas emissions or air pollution
        </p>
      </div>
      
      <div className="bg-white rounded-2xl p-6 border border-gray-200 text-center">
        <div className="w-16 h-16 bg-primary-green/10 rounded-2xl flex items-center justify-center mb-4 mx-auto">
          <FontAwesomeIcon icon={faBolt} className="text-2xl text-primary-green" />
        </div>
        <h4 className="text-lg font-bold text-dark mb-3">Reliable Power</h4>
        <p className="text-gray-600">
          Consistent and predictable energy generation with high efficiency rates
        </p>
      </div>
    </div>

    {/* CTA Section */}
    <div className="text-center">
      <div className="bg-gradient-to-r from-primary-green to-secondary-blue rounded-2xl p-12 text-white">
        <h3 className="text-3xl font-bold mb-6">Explore Hydroelectric Solutions</h3>
        <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
          Partner with us to harness the power of water for sustainable energy generation
        </p>
        <a 
          href="/contact" 
          className="btn bg-white text-primary-green hover:bg-gray-100 px-10 py-4 text-lg font-semibold inline-flex items-center justify-center gap-3"
        >
          Learn More About Hydro Power
          <FontAwesomeIcon icon={faArrowRight} className="text-lg" />
        </a>
      </div>
    </div>
  </div>
</section>

<section className="section bg-gray-50">
  <div className="container max-w-6xl">
    <div className="text-center mb-16">
      <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4">
        What We Offer
      </h2>
    </div>

    <div className="bg-white rounded-2xl p-8 mb-12 border border-gray-200">
      <p className="text-gray-700 leading-relaxed text-lg mb-8">
        At our core, we are dedicated to the building of the future of clean energy. We are 
        specialists at providing full solutions for the development, operation, and optimization of  <a href='/services/hydro'>hydroelectric energy projects.</a> Our expertise revolves around:
      </p>
      
      <div className="grid md:grid-cols-3 gap-8">
        <div className="bg-gradient-to-br from-primary-green/5 to-primary-green/10 rounded-2xl p-6">
          <div className="w-12 h-12 bg-primary-green rounded-xl flex items-center justify-center mb-4">
            <FontAwesomeIcon icon={faCogs} className="text-2xl text-white" />
          </div>
          <h3 className="text-xl font-bold text-dark mb-4">Engineering and Design</h3>
          <p className="text-gray-700">
            We conduct professional consultation and design work for existing and new hydropower plants, 
            focusing on sustainability and efficiency upgrades.
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-secondary-blue/5 to-secondary-blue/10 rounded-2xl p-6">
          <div className="w-12 h-12 bg-secondary-blue rounded-xl flex items-center justify-center mb-4">
            <FontAwesomeIcon icon={faProjectDiagram} className="text-2xl text-white" />
          </div>
          <h3 className="text-xl font-bold text-dark mb-4">Project Management</h3>
          <p className="text-gray-700">
            We carry out the complete project lifecycle of a hydropower project from the prefeasibility studies 
            up to the construction phase and commissioning and complete projects within budget and on schedule.
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-primary-green/5 to-secondary-blue/10 rounded-2xl p-6">
          <div className="w-12 h-12 bg-primary-green rounded-xl flex items-center justify-center mb-4">
            <FontAwesomeIcon icon={faTools} className="text-2xl text-white" />
          </div>
          <h3 className="text-xl font-bold text-dark mb-4">Maintenance and Upgrades</h3>
          <p className="text-gray-700">
            Our company offers ongoing maintenance and upgrades for facilities to perform optimally for 
            decades to come.
          </p>
        </div>
      </div>
    </div>

    {/* Benefits Section */}
    <div className="bg-gradient-to-r from-primary-green to-secondary-blue rounded-2xl p-8 lg:p-12 text-white">
      <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
        The Benefits of Hydroelectric Power in Odisha
      </h2>
      <p className="text-xl text-center mb-12 opacity-90 max-w-3xl mx-auto">
        Hydroelectric Power in Odisha offers a compelling set of advantages that make it a 
        cornerstone of a modern energy grid.
      </p>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4 mx-auto">
            <FontAwesomeIcon icon={faRecycle} className="text-2xl" />
          </div>
          <h3 className="text-xl font-bold mb-3">Renewable and Clean</h3>
        </div>
        
        <div className="text-center">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4 mx-auto">
            <FontAwesomeIcon icon={faShieldAlt 

            } className="text-2xl" />
          </div>
          <h3 className="text-xl font-bold mb-3">Reliable and Flexible</h3>
        </div>
        
        <div className="text-center">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4 mx-auto">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-2xl" />
          </div>
          <h3 className="text-xl font-bold mb-3">Long-Lasting Infrastructure</h3>
        </div>
        
        <div className="text-center">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4 mx-auto">
            <FontAwesomeIcon icon={faCog} className="text-2xl" />
          </div>
          <h3 className="text-xl font-bold mb-3">Multipurpose Functionality</h3>
        </div>
      </div>
    </div>

    {/* CTA Section */}
    <div className="text-center mt-12">
      <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200">
        <h3 className="text-2xl font-bold text-dark mb-4">Ready to Harness Water Power?</h3>
        <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
          Let's discuss how hydroelectric power can transform your energy strategy in Odisha
        </p>
        <a 
          href="/contact" 
          className="btn bg-primary-green text-white hover:bg-primary-green-dark px-8 py-3 font-semibold inline-flex items-center justify-center gap-2"
        >
          Start Your Hydro Project
          <FontAwesomeIcon icon={faArrowRight} />
        </a>
      </div>
    </div>
  </div>
</section>

      
    </div>
  );
};

export default Hydroelectric; 